import React, { useState, useEffect } from 'react';
import api from '../../api';
import AdminLayout from '../../components/AdminLayout';

const empty = {
  code: '', discount_pct: 10, max_uses: '', first_order_only: 1,
  active: 1, expires_at: ''
};

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => api.get('/payments/coupons').then(r => setCoupons(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await api.post('/payments/coupons', form);
      await load();
      setShowForm(false);
      setForm(empty);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create coupon');
    } finally { setSaving(false); }
  };

  const toggleActive = async (c) => {
    await api.put(`/payments/coupons/${c.id}`, { active: c.active ? 0 : 1 });
    load();
  };

  const del = async (c) => {
    if (!window.confirm(`Delete coupon "${c.code}"?`)) return;
    await api.delete(`/payments/coupons/${c.id}`);
    load();
  };

  const inp = { padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', width: '100%' };

  return (
    <AdminLayout title="Coupons">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Coupon Codes</h2>
          <p style={{ color: '#6b7280', fontSize: 14, margin: '4px 0 0' }}>Create and manage discount codes</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); setForm(empty); setError(''); }}>
          {showForm ? 'Cancel' : '+ New Coupon'}
        </button>
      </div>

      {showForm && (
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 24 }}>
          <h3 style={{ marginBottom: 20, fontSize: 16, fontWeight: 700 }}>Create New Coupon</h3>
          <form onSubmit={handleSave}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Code *</label>
                <input style={{ ...inp, textTransform: 'uppercase', fontFamily: 'monospace', letterSpacing: 1 }}
                  required value={form.code}
                  onChange={e => set('code', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g,''))}
                  placeholder="e.g. SUMMER20" maxLength={20} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Discount % *</label>
                <input style={inp} type="number" required min="1" max="100" value={form.discount_pct}
                  onChange={e => set('discount_pct', parseInt(e.target.value) || '')} placeholder="10" />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Max Uses (blank = unlimited)</label>
                <input style={inp} type="number" min="1" value={form.max_uses}
                  onChange={e => set('max_uses', e.target.value)} placeholder="Unlimited" />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Expires (optional)</label>
                <input style={inp} type="datetime-local" value={form.expires_at}
                  onChange={e => set('expires_at', e.target.value)} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, paddingTop: 24 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.first_order_only === 1}
                    onChange={e => set('first_order_only', e.target.checked ? 1 : 0)} />
                  First order only
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.active === 1}
                    onChange={e => set('active', e.target.checked ? 1 : 0)} defaultChecked />
                  Active
                </label>
              </div>
            </div>
            {error && <p style={{ color: '#ef4444', fontSize: 13, marginTop: 12 }}>{error}</p>}
            <div style={{ marginTop: 20 }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Creating...' : 'Create Coupon'}</button>
            </div>
          </form>
        </div>
      )}

      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f9fafb' }}>
            <tr>
              {['Code','Discount','Uses','Max Uses','First Order?','Expires','Status','Actions'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {coupons.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No coupons yet. Create your first one above.</td></tr>
            )}
            {coupons.map(c => (
              <tr key={c.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 15, color: '#4f46e5', letterSpacing: 1 }}>{c.code}</span>
                </td>
                <td style={{ padding: '12px 16px', fontWeight: 700, color: '#10b981' }}>{c.discount_pct}% off</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ fontWeight: 600 }}>{c.uses}</span>
                  {c.max_uses && <span style={{ color: '#9ca3af', fontSize: 12 }}> / {c.max_uses}</span>}
                </td>
                <td style={{ padding: '12px 16px', color: '#6b7280' }}>{c.max_uses || '—'}</td>
                <td style={{ padding: '12px 16px' }}>
                  {c.first_order_only ? <span style={{ fontSize: 12, background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '2px 8px' }}>Yes</span> : '—'}
                </td>
                <td style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280' }}>
                  {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{
                    fontSize: 12, borderRadius: 4, padding: '3px 10px', fontWeight: 600,
                    background: c.active ? '#dcfce7' : '#fee2e2',
                    color: c.active ? '#166534' : '#991b1b',
                  }}>{c.active ? 'Active' : 'Inactive'}</span>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm btn-outline" onClick={() => toggleActive(c)}>
                      {c.active ? 'Disable' : 'Enable'}
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => del(c)}>Del</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
